import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PRIMARY    = [26, 58, 107];
const SECONDARY  = [66, 133, 244];
const GREEN      = [58, 181, 74];
const GREEN_DARK = [34, 139, 34];
const AMBER      = [217, 119, 6];
const RED        = [229, 62, 62];
const LIGHT_BG   = [245, 247, 250];
const BORDER     = [220, 226, 235];
const TEXT       = [30, 40, 55];
const LIGHT_TEXT = [120, 130, 150];
const WHITE      = [255, 255, 255];

const stripHtml = (html) =>
  (html || '')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();

const getRatingColor = (rating) => {
  if (!rating) return LIGHT_TEXT;
  if (rating.includes('CS')) return GREEN_DARK;
  if (rating.includes('AS')) return AMBER;
  return RED;
};

const getGoalFinalScore = (g) => {
  const hod     = parseFloat(g.hodEndScore)     || 0;
  const manager = parseFloat(g.managerEndScore) || 0;
  const self    = parseFloat(g.selfEndScore)    || 0;
  if (hod     > 0) return hod;
  if (manager > 0) return manager;
  return self;
};

const stripEmoji = (str) => str ? str.split(' ')[0] : '--';

export const generateAppraisalPDF = async (formData, logoBase64) => {
  const doc     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW   = doc.internal.pageSize.getWidth();
  const pageH   = doc.internal.pageSize.getHeight();
  const margin  = 14;
  const cw      = pageW - margin * 2;
  let y         = margin;

  const addTopStrip = () => {
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, pageW, 8, 'F');
    doc.setFillColor(...SECONDARY);
    doc.rect(0, 7, pageW, 2, 'F');
  };

  const newPage = () => {
    doc.addPage();
    y = margin;
    addTopStrip();
    y = 14;
  };

  const checkBreak = (needed = 20) => {
    if (y + needed > pageH - 16) newPage();
  };

  const sectionBar = (title) => {
    checkBreak(14);
    doc.setFillColor(...PRIMARY);
    doc.roundedRect(margin, y, cw, 8, 1, 1, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...WHITE);
    doc.text(title, margin + 4, y + 5.5);
    y += 12;
  };

  const addFooters = () => {
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFillColor(...LIGHT_BG);
      doc.rect(0, pageH - 10, pageW, 10, 'F');
      doc.setDrawColor(...BORDER);
      doc.line(0, pageH - 10, pageW, pageH - 10);
      doc.setFontSize(7.5);
      doc.setTextColor(...LIGHT_TEXT);
      doc.setFont('helvetica', 'italic');
      doc.text('Density Pharmachem -- Performance Appraisal & Development Form', margin, pageH - 4);
      doc.text('Confidential', pageW / 2, pageH - 4, { align: 'center' });
      doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 4, { align: 'right' });
    }
  };

  // ===== HEADER =====
  addTopStrip();
  y = 12;

  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', margin, y, 38, 15); } catch (e) {}
  }

  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY);
  doc.text('Performance Appraisal &', pageW - margin, y + 5, { align: 'right' });
  doc.text('Development Form', pageW - margin, y + 12, { align: 'right' });
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LIGHT_TEXT);
  const periodLabel = formData.reviewPeriodFrom && formData.reviewPeriodTo
    ? `Review Period: ${new Date(formData.reviewPeriodFrom).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} -- ${new Date(formData.reviewPeriodTo).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`
    : 'Review Period: --';
  doc.text(periodLabel, pageW - margin, y + 18, { align: 'right' });
  y += 22;

  doc.setDrawColor(...SECONDARY);
  doc.setLineWidth(0.7);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ===== EMPLOYEE INFO =====
  sectionBar('Employee Information');

  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.roundedRect(margin, y, cw, 26, 1, 1, 'FD');

  const colW = cw / 4;
  const empItems = [
    { label: 'Employee Code',     value: formData.employeeCode     || '--' },
    { label: 'Employee Name',     value: formData.employeeName     || '--' },
    { label: 'Designation',       value: formData.designation      || '--' },
    { label: 'Department',        value: formData.department       || '--' },
    { label: 'Branch',            value: formData.branch           || '--' },
    { label: 'Date of Joining',   value: formData.dateOfJoining    || '--' },
    { label: 'Reporting Manager', value: formData.reportingManager || '--' },
    { label: 'HOD',               value: 'Ramakrishnan Sundaram'         },
  ];

  [[0,1,2,3],[4,5,6,7]].forEach((row, ri) => {
    row.forEach((itemIdx, ci) => {
      const item = empItems[itemIdx];
      const x  = margin + 3 + ci * colW;
      const ry = y + ri * 12;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...LIGHT_TEXT);
      doc.text(item.label.toUpperCase(), x, ry + 5);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEXT);
      const lines = doc.splitTextToSize(item.value, colW - 4);
      doc.text(lines[0] || '--', x, ry + 10);
    });
  });
  y += 30;

  // ===== SCORE CARDS =====
  const cardW = (cw - 8) / 3;
  const cards = [
    { label: 'OVERALL SCORE', value: formData.overallScore ? Number(formData.overallScore).toFixed(1) : '--', color: SECONDARY },
    { label: 'RATING',        value: formData.overallRating || '--', color: getRatingColor(formData.overallRating) },
    { label: 'STATUS',        value: formData.hodSubmitted ? 'Complete' : formData.managerSubmitted ? 'Manager Done' : formData.employeeSubmitted ? 'Emp Submitted' : 'Pending', color: GREEN },
  ];

  cards.forEach((card, i) => {
    const cx = margin + i * (cardW + 4);
    doc.setFillColor(...card.color);
    doc.roundedRect(cx, y, cardW, 16, 2, 2, 'F');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...WHITE);
    doc.text(String(card.value), cx + cardW / 2, y + 9, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(card.label, cx + cardW / 2, y + 14, { align: 'center' });
  });
  y += 22;

  // ===== SECTION 1 — GOALS =====
  sectionBar('Section 1 -- Performance Goals');

  const goals = formData.goals || [];
  goals.forEach((goal, gi) => {
    checkBreak(55);

    doc.setFillColor(...SECONDARY);
    doc.rect(margin, y, cw, 8, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...WHITE);
    const goalTitle = `Goal ${gi + 1}: ${(goal.title || '').replace(/^Goal:\s*\d+\s*/, '').trim() || `Goal ${gi + 1}`}`;
    doc.text(goalTitle, margin + 3, y + 5.5);
    doc.text(`Weightage: ${goal.weightage || 0}%`, pageW - margin - 3, y + 5.5, { align: 'right' });
    y += 10;

    const goalDescription = stripHtml(goal.description);
    if (goalDescription) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...TEXT);
      const descLines = doc.splitTextToSize(`Description: ${goalDescription}`, cw - 4);
      checkBreak(descLines.length * 4 + 4);
      doc.text(descLines, margin + 2, y + 4);
      y += descLines.length * 4 + 4;
    }
    if (goal.timeline) {
      doc.setFontSize(8);
      doc.setTextColor(...LIGHT_TEXT);
      doc.text(`Timeline: ${goal.timeline}`, margin + 2, y + 3);
      y += 6;
    }

    checkBreak(40);
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Review Type', 'Comments', 'Achievement %', 'Score']],
      body: [
        ['Self Mid-Year',     goal.selfMidComments    || '--', `${goal.selfMidPercentage    || 0}%`, Number(goal.selfMidScore    || 0).toFixed(1)],
        ['Manager Mid-Year',  goal.managerMidComments || '--', `${goal.managerMidPercentage || 0}%`, Number(goal.managerMidScore || 0).toFixed(1)],
        ['Self End-Year',     goal.selfEndComments    || '--', `${goal.selfEndPercentage    || 0}%`, Number(goal.selfEndScore    || 0).toFixed(1)],
        ['Manager End-Year',  goal.managerEndComments || '--', `${goal.managerEndPercentage || 0}%`, Number(goal.managerEndScore || 0).toFixed(1)],
        ['HOD End-Year',      goal.hodEndComments     || '--', `${goal.hodEndPercentage     || 0}%`, Number(goal.hodEndScore     || 0).toFixed(1)],
      ],
      foot: [[
        { content: 'Final Goal Score', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fillColor: LIGHT_BG, textColor: TEXT } },
        { content: getGoalFinalScore(goal).toFixed(1), styles: { fontStyle: 'bold', textColor: PRIMARY, fillColor: LIGHT_BG } },
      ]],
      headStyles:  { fillColor: PRIMARY, textColor: WHITE, fontSize: 8, fontStyle: 'bold', cellPadding: 3 },
      bodyStyles:  { fontSize: 8, textColor: TEXT, cellPadding: 3 },
      footStyles:  { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 18, halign: 'center' },
      },
      alternateRowStyles: { fillColor: [250, 251, 253] },
      showFoot: 'lastPage',
    });
    y = doc.lastAutoTable.finalY + 7;
  });

  // ===== SECTION 2 — FEEDBACK =====
  checkBreak(20);
  sectionBar('Section 2 -- Employee, Manager & HOD Feedback');

  // Satisfaction
  if (formData.satisfactionMidYear || formData.satisfactionEndYear) {
    checkBreak(12);
    doc.setFillColor(...LIGHT_BG);
    doc.setDrawColor(...BORDER);
    doc.roundedRect(margin, y, cw, 10, 1, 1, 'FD');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT);
    doc.text(`Job Satisfaction (Mid-Year): ${stripEmoji(formData.satisfactionMidYear)}`, margin + 4, y + 6.5);
    doc.text(`Job Satisfaction (End-Year): ${stripEmoji(formData.satisfactionEndYear)}`, pageW / 2 + 4, y + 6.5);
    y += 13;
  }

  // Comments table
  checkBreak(30);
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Period', 'Employee Comment', 'Manager Comment', 'HOD Comment']],
    body: [
      ['Mid-Year', formData.employeeMidComment || '--', formData.managerMidComment || '--', formData.hodMidComment || '--'],
      ['End-Year', formData.employeeEndComment || '--', formData.managerEndComment || '--', formData.hodEndComment || '--'],
    ],
    headStyles:  { fillColor: SECONDARY, textColor: WHITE, fontSize: 8, fontStyle: 'bold', cellPadding: 3 },
    bodyStyles:  { fontSize: 8, textColor: TEXT, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 20, fontStyle: 'bold' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 'auto' },
    },
    alternateRowStyles: { fillColor: [250, 251, 253] },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Ratings
  checkBreak(40);
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.roundedRect(margin, y, cw, 8, 1, 1, 'FD');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY);
  doc.text('Overall Ratings -- End Year', margin + 4, y + 5.5);
  y += 10;

  // Rating info bar
  const ratingInfoW = cw / 3;
  const ratingColors = [[229,62,62], [217,119,6], [34,139,34]];
  const ratingLabels = ['RI -- Requires Improvement (below 65)', 'AS -- At Standard (65-90)', 'CS -- Clear Strength (above 90)'];
  ratingLabels.forEach((label, i) => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...ratingColors[i]);
    doc.text(label, margin + 4 + i * ratingInfoW, y + 4);
  });
  y += 8;

  const ratingRows = [
    { label: 'Self End-Year Rating',     value: formData.selfEndRating    || '--' },
    { label: 'Reporting Manager Rating', value: formData.managerEndRating || '--' },
    { label: 'HOD Rating',               value: formData.hodEndRating     || '--' },
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Reviewer', 'Rating Given']],
    body: ratingRows.map(r => [r.label, r.value]),
    headStyles:  { fillColor: PRIMARY, textColor: WHITE, fontSize: 8, fontStyle: 'bold', cellPadding: 3 },
    bodyStyles:  { fontSize: 8, textColor: TEXT, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 'auto' } },
    alternateRowStyles: { fillColor: [250, 251, 253] },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ===== SIGN OFF =====
  checkBreak(70);
  sectionBar('Appraisal Sign Off');

  const signoffData = [
    { name: formData.employeeName || 'Employee', goals: formData.employeeGoalsDate || '2025-04-01', mid: formData.employeeMidDate || '2025-09-30', end: formData.employeeEndDate || '2026-03-31', signature: formData.employeeSignature || null },
    { name: formData.reportingManager || 'Reporting Manager', goals: formData.managerGoalsDate || '2025-04-01', mid: formData.managerMidDate || '2025-09-30', end: formData.managerEndDate || '2026-03-31', signature: formData.managerSignature || null },
    { name: 'Ramakrishnan Sundaram (HOD)', goals: formData.hodGoalsDate || '2025-04-01', mid: formData.hodMidDate || '2025-09-30', end: formData.hodEndDate || '2026-03-31', signature: formData.hodSignature || null },
  ];

  const cols   = [48, 28, 28, 28, 46];
  const headers = ['Name', 'Goals Setup', 'Mid-Year', 'End Year', 'Signature'];
  const rowH   = 24;

  doc.setFillColor(...PRIMARY);
  doc.rect(margin, y, cw, 8, 'F');
  let hx = margin;
  headers.forEach((h, i) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...WHITE);
    doc.text(h, hx + 2, y + 5.5);
    hx += cols[i];
  });
  y += 9;

  signoffData.forEach((row, ri) => {
    checkBreak(rowH + 2);
    const rowBg = ri % 2 === 0 ? WHITE : LIGHT_BG;
    doc.setFillColor(...rowBg);
    doc.setDrawColor(...BORDER);
    doc.rect(margin, y, cw, rowH, 'FD');

    let rx = margin;

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEXT);
    const nameLines = doc.splitTextToSize(row.name, cols[0] - 4);
    doc.text(nameLines, rx + 2, y + 7);
    rx += cols[0];

    [row.goals, row.mid, row.end].forEach((date, di) => {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...TEXT);
      doc.text(date || '--', rx + 2, y + 7);
      rx += cols[di + 1];
    });

    if (row.signature && row.signature.startsWith('data:image')) {
      try {
        const imgType = row.signature.includes('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(row.signature, imgType, rx + 2, y + 2, 40, 18);
      } catch (e) {
        doc.setFontSize(7);
        doc.setTextColor(...LIGHT_TEXT);
        doc.text('Signature on file', rx + 2, y + 12);
      }
    } else {
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...LIGHT_TEXT);
      doc.text('Not signed', rx + 2, y + 12);
    }

    y += rowH + 1;
  });

  y += 6;

  // ===== FOOTER NOTE =====
  checkBreak(16);
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.roundedRect(margin, y, cw, 12, 1, 1, 'FD');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...LIGHT_TEXT);
  doc.text('This document is confidential and intended for internal use only.', margin + 4, y + 5);
  const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  doc.text(`Generated on: ${genDate}`, margin + 4, y + 10);
  doc.text('Density Pharmachem Pvt. Ltd.', pageW - margin - 4, y + 10, { align: 'right' });

  addFooters();

  const fileName = `Appraisal_${(formData.employeeName || 'Employee').replace(/\s+/g, '_')}_2025-26.pdf`;
  doc.save(fileName);
};